import http from '../http-common';

const clientInStorage = localStorage.getItem('selectedClientId');
let sessionClientId = clientInStorage ? clientInStorage : '00000000-0000-0000-0000-000000000000';

const userInStorage = localStorage.getItem('user');
let sessionUser = userInStorage ? JSON.parse(userInStorage) : {};

const roleInStorage = localStorage.getItem('role');
let sessionRole = roleInStorage ?? '';

const reportingRoleInStorage = localStorage.getItem('reportingRole');
let sessionReportingRole = reportingRoleInStorage ?? '';

class DataService {
  getClientId() {
    return sessionClientId;
  }

  updateClientId(clientId) {
    localStorage.setItem('selectedClientId', clientId);
    sessionClientId = clientId;
  }

  getSessionUser() {
    return sessionUser;
  }

  updateSessionUser(user) {
    sessionUser = user;
  }

  getRole() {
    return sessionRole;
  }

  updateSessionRole(role) {
    sessionRole = role;
  }

  getReportingRole() {
    return sessionReportingRole;
  }

  updateSessionReportingRole(role) {
    sessionReportingRole = role;
  }

  async getClientDetails() {
    const response = await http.get(`/clients/${sessionClientId}`);
    return response.data;
  }

  async setTokenHeader() {
    const token = localStorage.getItem('token');
    const response = await this.validateToken({ 'token': token });

    if (!response.data.valid_token) {
      this.logout();
      return;
    } else {
      localStorage.setItem('token', response.data.newToken);
    } 

    // hack to fix the headers when the app first loads.
    // https://stackoverflow.com/questions/47715451/localstorage-item-not-updating-in-axios-headers
    if (localStorage.getItem('token')) {
      http.defaults.headers['x-access-token'] = localStorage.getItem('token');
    }
  }

  logout() {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('clientIds');
    localStorage.removeItem('role');
    localStorage.removeItem('reportingRole');
    sessionUser = {};
    sessionRole = '';
    sessionReportingRole = '';

    window.location.href = '/#/login';
  }

  // ========== Auth Functions =================

  validateToken(body) {
    if (localStorage.getItem('token')) {
      http.defaults.headers['x-access-token'] = localStorage.getItem('token');
    }
    return http.post('/auth/validateToken', body);
  }

  validate2FA(body) {
    if (localStorage.getItem('token')) {
      http.defaults.headers['x-access-token'] = localStorage.getItem('token');
    }
    return http.post('/auth/validate2FA', body);
  }

  async changeClientSelection(clientId) {
    this.setTokenHeader();
    const response = await http.post('/auth/changeClient', { clientId });
    if (response.status !== 200) {
      throw new Error('Failed to change client');
    } else {
      localStorage.setItem('token', response.data.token);
      this.updateClientId(clientId);
    } 

    // hack to fix the headers when the app first loads.
    // https://stackoverflow.com/questions/47715451/localstorage-item-not-updating-in-axios-headers
    if (localStorage.getItem('token')) {
      http.defaults.headers['x-access-token'] = localStorage.getItem('token');
    }
  }

  // ========== Real User Functions ================

  createClientUserAccount(body) {
    this.setTokenHeader();
    return http.post(`/${sessionClientId}/users`, body);
  }

  getClientUserAccounts(params) {
    this.setTokenHeader();
    return http.get(`/${sessionClientId}/users/${ params }`);
  }

  deleteClientUserAccount(userId) {
    this.setTokenHeader();
    return http.delete(`/${sessionClientId}/users/${userId}`);
  }

  updateUser(userId, body) {
    this.setTokenHeader();
    return http.put(`/${sessionClientId}/users/${userId}`, body);
  }

  // ========== End Auth Functions =================

  // users functions
  getAllUsers(params) {
    // hack to fix the headers when the app first loads.
    // https://stackoverflow.com/questions/47715451/localstorage-item-not-updating-in-axios-headers
    if (localStorage.getItem('token'))
      http.defaults.headers['x-access-token'] = localStorage.getItem('token');
    return http.get('/users', { params });
  }

  getUserById(id) {
    this.setTokenHeader();
    return http.get(`/user/${id}`);
  }

  getUserByEmail(email) {
    this.setTokenHeader();
    return http.get(`/patient_user/${email}`);
  }

  updateUserPassword(params) {
    this.setTokenHeader();
    return http.patch('/update_password', params);
  }

  deleteUser(id) {
    this.setTokenHeader();
    return http.delete(`/users/${id}`);
  }

  addUser(body) {
    this.setTokenHeader();
    return http.post('/users/', {body});
  }

  addPatientUser(body) {
    return http.post(`/clients/${sessionClientId}/patientUser`, body);
  }

  createInvitation(body) {
    this.setTokenHeader();
    return http.post(`/auth/${sessionClientId}/invite_user`, body);
  }

  connectClientUserToPatient(body) {
    this.setTokenHeader();
    return http.post('/connect_user_patient', body);
  }

  send_sms(body) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/sms`, body);
  }

  fetchMessages(params) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/sms`, { params });
  }

  getUnreadMessageCount() {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/sms/unread/count`);
  }

  markSmsAsRead(messageId) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/messages/${messageId}/read`);
  }

  markSmsAsUnread(messageId) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/messages/${messageId}/unread`);
  }

  getUserClients() {
    return http.get('/user/clients');
  }

  getConfigInfoById(id) {
    this.setTokenHeader();
    return http.get(`/config_get/${id}`);
  }

  getClientPreferences(clientId) {
    return http.get(`/config_get/${clientId || sessionClientId}/preferences`);
  }

  getClientReferralMethods(clientId) {
    return http.get(`/clients/${clientId || sessionClientId}/referralMethods`);
  }

  getConfigLogo(clientId) {
    this.setTokenHeader();
    return http.get(`/config_get/${clientId ?? sessionClientId}/logo`);
  }

  updateConfigInfoById(id, body) {
    this.setTokenHeader();
    return http.put(`/config_update/${id}`, body);
  }

  updateClientInfo(id, body) {
    this.setTokenHeader();

    return http.put(`/clients/${id}`, body);
  }

  checkAuthKeys(params) {
    this.setTokenHeader();
    return http.get('/check_auth_keys', { params });
  }

  getServiceMessages() {
    return http.get(`/clients/${sessionClientId}/serviceMessages`);
  }

  updateServiceMessages(body) {
    this.setTokenHeader();
    return http.put(`/clients/${sessionClientId}/serviceMessages`, body);
  }

  getOpenDentalVersion(clientId) {
    this.setTokenHeader();
    return http.get(`/clients/${clientId ?? sessionClientId}/openDentalVersion`);
  }

  getReportingRoles() {
    this.setTokenHeader();
    return http.get('/reportingRoles');
  }

  getProductionReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/acrew/production?start=${start}&end=${end}`);
  }

  getActiveUserReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/users/active?start=${start}&end=${end}`);
  }

  getNewUserReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/users/new?start=${start}&end=${end}`);
  }

  getOnlinePaymentReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/acrew/payments?start=${start}&end=${end}`);
  }

  getNewClaimReport() {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/claims/new`);
  }

  getClinicEIEVReport({ month }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/estimatedIdealExamVolume/clinic?month=${month}`);
  }

  getClinicHygieneReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/hygiene/clinic?start=${start}&end=${end}`);
  }

  getClinicOverviewReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/overview/clinic?start=${start}&end=${end}`);
  }

  getDDSChairUtilizationReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/chairUtilization/dds?start=${start}&end=${end}`);
  }

  getDDSPatientsReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/patients/dds?start=${start}&end=${end}`);
  }

  getDDSTreatmentsReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/treatments/dds?start=${start}&end=${end}`);
  }

  getDDSProductionReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/production/dds?start=${start}&end=${end}`);
  }

  getDDSCollectionsReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/collections/dds?start=${start}&end=${end}`);
  }

  getRDHChairUtilizationReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/chairUtilization/rdh?start=${start}&end=${end}`);
  }

  getRDHPatientsReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/patients/rdh?start=${start}&end=${end}`);
  }

  getRDHTreatmentsReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/treatments/rdh?start=${start}&end=${end}`);
  }

  getRDHProductionReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/production/rdh?start=${start}&end=${end}`);
  }

  getRDHCollectionsReport({ start, end }) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/reporting/collections/rdh?start=${start}&end=${end}`);
  }

  getClientQueries() {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/queries`);
  }

  getClientQueryResult(id) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/queries/${id}/result`);
  }

  //==========Appointments=======================

  getAppointmentTypes() {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/appointmentTypes`);
  }

  updateAppointmentType(appointmentType) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/appointmentTypes/${appointmentType.id}`, appointmentType);
  }

  createAppointmentType(appointmentType) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/appointmentTypes`, appointmentType);
  }

  getAppointmentType(appointmentTypeId) {
    return http.get(`/clients/${sessionClientId}/appointmentTypes/${appointmentTypeId}`);
  }

  deleteAppointmentType(appointmentTypeId) {
    this.setTokenHeader();
    return http.delete(`/clients/${sessionClientId}/appointmentTypes/${appointmentTypeId}`);
  }

  getSchedulableAppointmentTypes(preferredClientId, params) {
    const queryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      queryParams.append(key, value);
    }

    return http.get(`/clients/${preferredClientId ?? sessionClientId}/schedulableAppointmentTypes?${queryParams.toString()}`);
  }

  getAvailabilityForAppointmentType(preferredClientId, appointmentTypeId, startDate, endDate) {
    return http.get(`/clients/${preferredClientId ?? sessionClientId}/appointmentTypes/${appointmentTypeId}/availability?startDate=${startDate}&endDate=${endDate}`);
  }

  getAvailabilityForCustomAppointment(preferredClientId, providerId, duration, startDate, endDate, pattern) {
    return http.get(`/clients/${preferredClientId ?? sessionClientId}/providers/${providerId}/availability/${duration}?startDate=${startDate}&endDate=${endDate}&pattern=${pattern}`);
  }

  scheduleAppointment(body, token) {
    const headers = {};
    if (token) {
      headers['x-access-token'] = token;
    }

    return http.post(`/clients/${sessionClientId}/appointments`, body, { headers });
  }

  //==========Documents==========================

  getAllDocuments(params) {
    this.setTokenHeader();
    return http.get('/all_documents', { params });
  }

  updateFilename(params) {
    this.setTokenHeader();
    return http.get('/update_filename', { params });
  }

  getDocumentsById(params) {
    this.setTokenHeader();
    return http.get('/documents_by_id', { params });
  }

  getAllUploads() {
    this.setTokenHeader();
    return http.get('/all_uploads');
  }

  createFile(params) {
    this.setTokenHeader();
    return http.get('/create_file', { params });
  }

  removeFile(params) {
    this.setTokenHeader();
    return http.get('/remove_file', { params });
  }

  deleteFile(params) {
    this.setTokenHeader();
    return http.post('/delete_file', params);
  }

  downloadFile(params) {
    this.setTokenHeader();
    return http.post('/download_file', params);
  }

  uploadDocumentAdmin(file, directory) {
    this.setTokenHeader();
    return http.post(`/document/upload/${directory}`, file);
  }

  //========== Employees ========================

  getAllEmployees() {
    this.setTokenHeader();
    return http.get('/all_employees');
  }

  getEmails(params) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/emails`, { params });
  }

  // ========= Template Functions =========================

  sendTemplateImmediate(id) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/templates/${id}/send`);
  }

  createTemplate(params) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/templates`, params);
  }

  getGenericTemplate(params) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/templates/generic`, params);
  }

  getTemplates(params) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/templates`, params);
  }

  getTemplateById(id) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/templates/${id}`);
  }

  updateTemplate(params) {
    this.setTokenHeader();
    return http.put(`/clients/${sessionClientId}/templates/${params.id}`, params);
  }

  deleteTemplate(id) {
    this.setTokenHeader();
    return http.delete(`/clients/${sessionClientId}/templates/${id}`);
  }

  // ========= Form Functions =========================

  createForm(params) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/forms`, params);
  }

  getClientForms() {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/forms`);
  }

  getGenericForm(params) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/forms/generic`, params);
  }

  getFormById(id) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/forms/${id}`);
  }

  updateForm(id, params) {
    this.setTokenHeader();
    return http.put(`/clients/${sessionClientId}/forms/${id}`, params);
  }

  deleteForm(id) {
    this.setTokenHeader();
    return http.delete(`/clients/${sessionClientId}/forms/${id}`);
  }

  searchForPatient(clientId, params) {
    return http.post(`/clients/${clientId}/patients/search`, params);
  }

  getUser(id) {
    this.setTokenHeader();
    return http.get(`/user/${id}`);
  }

  getFormsForUser(id) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/users/${id}/forms`);
  }

  updateFormsForUser(id, params) {
    this.setTokenHeader();
    return http.put(`/clients/${sessionClientId}/users/${id}/forms`, params);
  }

  assignFormForUser(id, formId) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/users/${id}/forms/${formId}`);
  }

  // ========= CONTACT PREFERENCES FUNCTIONS ===============

  getPatientContactPreferences() {
    this.setTokenHeader();
    return http.get('/patient/contactPreferences');
  }

  getFormsForPatient(id) {
    this.setTokenHeader();

    let route = '/patient/forms';
    if (typeof id === 'number' || typeof id === 'string') {
      route = `/patient/dependents/${id}/forms`;
    }

    return http.get(route);
  }

  getTreatmentPlansForPatient(id) {
    this.setTokenHeader();

    let route = '/patient/treatmentPlans';
    if (typeof id === 'number' || typeof id === 'string') {
      route = `/patient/dependents/${id}/treatmentPlans`;
    }

    return http.get(route);
  }

  saveUserFormInput(userFormId, id, pdfContent) {
    this.setTokenHeader();

    let route = `/patient/forms/${userFormId}`;
    if (typeof id === 'number' || typeof id === 'string') {
      route = `/patient/dependents/${id}/forms/${userFormId}`;
    }

    return http.post(route, { base64PdfContent: pdfContent });
  }

  setPatientContactPreferences(body) {
    return http.put('/patient/contactPreferences', body);
  }

  getEmailContactPreferences(clientId, userEmail) {
    return http.get(`/clients/${clientId}/email/${userEmail}/contactPreferences`);
  }

  setEmailContactPreferences(clientId, userEmail, body) {
    return http.put(`/clients/${clientId}/email/${userEmail}/contactPreferences`, body);
  }

  getOpenDentalOperatories() {
    this.setTokenHeader();
    return http.get(`/od_api/${sessionClientId}/operatories`);
  }

  // ========= NEW PATIENT REGISTRATION (OPENDENTAL API FUNCTIONS) ===============

  getLastPatientForm(patientId) {
    this.setTokenHeader();
    return http.get(
      `/od_api/${sessionClientId}/new_patient_registration/last_form/${patientId}`,
    );
  }

  postNewPatRegistration(params, userId) {
    this.setTokenHeader();
    return http.post(
      `/od_api/${sessionClientId}/new_patient_registration/${userId}`,
      params,
    );
  }

  getAllergyDefinitions() {
    this.setTokenHeader();
    return http.get(
      `/od_api/${sessionClientId}/new_patient_registration/get_allergies`,
    );
  }

  getMedicationDefinitions() {
    this.setTokenHeader();
    return http.get(
      `/od_api/${sessionClientId}/new_patient_registration/get_medications`,
    );
  }

  getClientDiseaseCategories() {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/diseaseCategories`);
  }

  createClientDiseaseCategory(category) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/diseaseCategories`, category);
  }

  updateClientDiseaseCategory(id, category) {
    this.setTokenHeader();
    return http.put(`/clients/${sessionClientId}/diseaseCategories/${id}`, category);
  }

  deleteClientDiseaseCategory(id) {
    this.setTokenHeader();
    return http.delete(`/clients/${sessionClientId}/diseaseCategories/${id}`);
  }

  getDiseaseDefinitions() {
    this.setTokenHeader();
    return http.get(
      `/od_api/${sessionClientId}/new_patient_registration/get_diseases`,
    );
  }

  getOtherConditions() {
    this.setTokenHeader();
    return http.get(
      `/od_api/${sessionClientId}/new_patient_registration/get_other_conditions`,
    );
  }

  getQuestions() {
    this.setTokenHeader();
    return http.get(
      `/od_api/${sessionClientId}/new_patient_registration/get_questions`,
    );
  }

  getPatientDiseases(patientId) {
    this.setTokenHeader();

    let route = '/patient/diseases';
    if (typeof patientId === 'number' || typeof patientId === 'string') {
      route = `/patient/dependents/${patientId}/diseases`;
    }

    return http.get(route);
  }

  getPatientAllergies(patientId) {
    this.setTokenHeader();

    let route = '/patient/allergies';
    if (typeof patientId === 'number' || typeof patientId === 'string') {
      route = `/patient/dependents/${patientId}/allergies`;
    }

    return http.get(route);
  }

  getPatientMedications(patientId) {
    this.setTokenHeader();

    let route = '/patient/medications';
    if (typeof patientId === 'number' || typeof patientId === 'string') {
      route = `/patient/dependents/${patientId}/medications`;
    }

    return http.get(route);
  }

  getPatients(params) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/patients${ params }`);
  }

  find_patient(body) {
    this.setTokenHeader();
    return http.post(`/od_api/${sessionClientId}/find_patient`, body);
  }

  createPatient(body) {
    this.setTokenHeader();
    return http.post(`/clients/${sessionClientId}/patients`, body);
  }

  getPatientById(patientId) {
    this.setTokenHeader();
    return http.get(`/clients/${sessionClientId}/patients/${patientId}`);
  }

  updateClientPatientInfo(patientId, data) {
    this.setTokenHeader();
    return http.put(`/clients/${sessionClientId}/patients/${patientId}`, data);
  }
  
  getPatientSmsLog(patientId) {
    return http.get(`/clients/${sessionClientId}/patients/${patientId}/messages`);
  }
  
  sendPatientSms(patientId, message) {
    return http.post(`/clients/${sessionClientId}/patients/${patientId}/messages`, { message });
  }

  getPatientAccountBalance(patientId) {
    this.setTokenHeader();

    let route = '/patient/accountBalance';
    if (patientId) {
      route = `/clients/${sessionClientId}/patients/${patientId}/accountBalance`;
    }

    return http.get(route);
  }

  getPatientAccountBreakdown(patientId) {
    this.setTokenHeader();

    let route = '/patient/accountBreakdown';
    if (patientId) {
      route = `/clients/${sessionClientId}/patients/${patientId}/accountBreakdown`;
    }

    return http.get(route);
  }

  getPatientAppointments() {
    this.setTokenHeader();
    return http.get('/patient/appointments');
  }

  scheduleAppointmentForPatient(body, id) {
    this.setTokenHeader();

    let route = '/patient/appointments';
    if (typeof id === 'number' || typeof id === 'string') {
      route = `/patient/dependents/${id}/appointments`;
    }

    return http.post(route, body);
  }

  getPatientInfo() {
    this.setTokenHeader();
    return http.get('/patient');
  }

  updatePatientInfo(id, data) {
    this.setTokenHeader();

    let route = '/patient';
    if (typeof id === 'number' || typeof id === 'string') {
      route = `/patient/dependents/${id}`;
    } else {
      data = id;
    }

    return http.put(route, data);
  }

  getPatientFamily(patientId) {
    this.setTokenHeader();

    let route = '/patient/family';
    if (patientId) {
      route = `/clients/${sessionClientId}/patients/${patientId}/family`;
    }

    return http.get(route);
  }

  getPatientConsent(patientId) {
    this.setTokenHeader();

    let route = '/patient/consent';
    if (patientId) {
      route = `/clients/${sessionClientId}/patients/${patientId}/consent`;
    }

    return http.get(route);
  }

  getEligiblePatientsForConsent(patientId) {
    this.setTokenHeader();

    let route = '/patient/consent/eligible';
    if (patientId) {
      route = `/clients/${sessionClientId}/patients/${patientId}/consent/eligible`;
    }

    return http.get(route);
  }

  revokePatientConsent(viewablePatientId, viewingPatientId) {
    this.setTokenHeader();

    let route;
    if (!viewingPatientId) {
      route = `/patient/consent/${viewablePatientId}/revoke`;
    } else {
      route = `/clients/${sessionClientId}/patients/${viewablePatientId}/consent/${viewingPatientId}/revoke`;
    }

    return http.post(route);
  }

  grantPatientViewingAccess(viewablePatientId, viewingPatientId) {
    this.setTokenHeader();

    let route;
    if (!viewingPatientId) {
      route = `/patient/consent/${viewablePatientId}/grant`;
    } else {
      route = `/clients/${sessionClientId}/patients/${viewablePatientId}/consent/${viewingPatientId}/grant`;
    }

    return http.post(route);
  }

  updatePatientConsent(body) {
    this.setTokenHeader();
    return http.put('/patient/consent', body);
  }

  getPaymentUrl(amount) {
    this.setTokenHeader();
    return http.get('/patient/paymentUrl', { amount });
  }

  savePayment({ referenceId }) {
    this.setTokenHeader();
    return http.post('/patient/payment', { referenceId });
  }

  getLastFormForCurrentPatient() {
    this.setTokenHeader();
    return http.get('/od_api/new_patient_registration/last_form');
  }

  // ========= OPENDENTAL APPOINTMENT API FUNCTIONS ===============

  get_appointment_by_id(id) {
    this.setTokenHeader();
    return http.get(`/od_api/${sessionClientId}/get_appointment_by_id/${id}`);
  }

  find_appointment(body) {
    this.setTokenHeader();
    return http.post(`/od_api/${sessionClientId}/find_appointment`, body);
  }

  new_appointment(body) {
    this.setTokenHeader();
    return http.post(`/od_api/${sessionClientId}/new_appointment`, body);
  }

  // =========PROVIDER (OPENDENTAL API FUNCTIONS) ===============

  get_providers(params) {
    this.setTokenHeader();
    return http.get(`/od_api/${sessionClientId}/get_providers/${ params }`);
  }

  get_provider_by_id(id) {
    this.setTokenHeader();
    return http.get(`/od_api/${sessionClientId}/get_provider_by_id/${id}`);
  }

  find_provider(body) {
    this.setTokenHeader();
    return http.post(`/od_api/${sessionClientId}/find_provider`, body);
  }

  new_provider(body) {
    this.setTokenHeader();
    return http.post(`/od_api/${sessionClientId}/new_provider`, body);
  }

  update_provider(id, body) {
    this.setTokenHeader();
    return http.post(`/od_api/${sessionClientId}/update_provider/${id}`, body);
  }

  async getAvailableClients() {
    return http.get('/clients');
  }

  // =========USER (OPENDENTAL API FUNCTIONS)===============

  get_users(params) {
    this.setTokenHeader();
    return http.get('/od_api/get_users', params);
  }

  get_user_by_id(id) {
    this.setTokenHeader();
    return http.get(`/od_api/get_user_by_id/${id}`);
  }

  find_user(body) {
    this.setTokenHeader();
    return http.post('/od_api/find_user', body);
  }

  new_user(body) {
    this.setTokenHeader();
    return http.post('/od_api/new_user', body);
  }

  update_user(id, body) {
    this.setTokenHeader();
    return http.post(`/od_api/update_user/${id}`, body);
  }

  // ========= END OPENDENTAL API FUNCTIONS ===============

  sendPasswordResetEmail(username) {
    return http.post('/auth/send_password_reset_email', username);
  }

  sendNewUserRequestEmail(body) {
    return http.post('/auth/send_new_user_req_email', body);
  }

  checkPasswordResetToken(token) {
    return http.post('/auth/check_reset_token', token);
  }

  changePassword(body) {
    return http.post('/auth/change_password', body);
  }

  changeAndSendPassword(body) {
    return http.post('/auth/admin_change_and_send_password', body);
  }

  createAccount(clientId, body, authToken) {
    return http.post(`/auth/${clientId || sessionClientId}/create_account`, body, { headers: { ['x-access-token']: authToken } });
  }

  updateAccountInfo(body) {
    return http.put('/auth/account', body);
  }

  forgot_password(email) {
    return http.post('/auth/forgot_password', { email });
  }

  // ========= END AUTH FUNCTIONS ================

  signin(email, password) {
    return http.post('/auth/signin', { email, password });
  }

  regenerate2FA(token, forceEmail) {
    return http.post('/auth/regenerate2FA', { token, forceEmail });
  }

  verifyOTP(body) {
    return http.post('/auth/validate2FA', body);
  }

  getPatientValidationToken(clientId, patientId) {
    return http.get(`/auth/clients/${clientId}/patients/${patientId}/validate`);
  }

  verifyPatientValidationToken(clientId, patientId, token, otp) {
    return http.post(`/auth/clients/${clientId}/patients/${patientId}/validate`, { token, otp });
  }
}

export default new DataService();
